import {html, LitElement, PropertyValues} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import {defaultSettings, Settings} from './models/fishman-update-password.model';

import {a11Fetch, A11SiteDataModel, FetchProps} from './utilities';

@customElement('fishman-update-password')
export class FishmanUpdatePassword extends LitElement {

    @property({type: String})
    encodedSettings = '';

    @property({type: Object})
    settings: Settings = {...defaultSettings};

    @property({type: Boolean})
    is_form_submitting = false;

    @property({type: Boolean})
    is_successful = false;

    @property({type: Boolean})
    has_form_validation_error = false;

    @property({type: String})
    form_validation_error_message = '';

    @property({type: String})
    email = '';

    @property({type: String})
    errorMessage = '';

    override updated(changedProperties: PropertyValues) {
        if (!changedProperties.has('encodedSettings')) {
            return;
        }

        let decodedSettings;

        try {
            const rawSettings = atob(this.encodedSettings); // decode base64 string
            decodedSettings = this.settings = JSON.parse(rawSettings); // parse the JSON string into an object
        } catch (e) {
            console.error('Cannot parse settings:', e);
        }

        if (decodedSettings?.email) {
            this.email = decodedSettings.email;
        }
    }

    override render() {
        // Destructure the settings object for easy access to the properties
        const {
            code,
            password_requirements,
            password_length,
        } = this.settings;

        let password_regex = `^`;
        let error_message = ``;
        const password_length_min = password_length && password_length < 4 ? 4 : password_length;

        error_message = password_requirements && password_requirements.length > 0 ? `Password must contain ` : ``;
        if (password_requirements && password_requirements.includes('uppercase')) {
            password_regex += `(?=.*[A-Z])`;
            error_message += `at least one uppercase letter, `;
        }
        if (password_requirements && password_requirements.includes('lowercase')) {
            password_regex += `(?=.*[a-z])`;
            error_message += `at least one lowercase letter, `;
        }
        if (password_requirements && password_requirements.includes('number')) {
            password_regex += `(?=.*[0-9])`;
            error_message += `at least one number, `;
        }
        if (password_requirements && password_requirements.includes('special_character')) {
            password_regex += `(?=.*[\\!@#\\$%\\^&\\*\\(\\)\\-\\+\\?~])`;
            error_message += `at least one special character (!@#$%^&*()-+?~), `;
        }
        password_regex += `.{${password_length_min},}$`;
        if (password_requirements && password_requirements.length > 0) {
            error_message = error_message.slice(0, -2) + `. `;
        }
        error_message += `Password must be at least ${password_length} characters long.`;

        if ((!this.email) && !window?.a11_localized_data?.is_in_wp_admin) {
            return html`
                <div class="alert alert-danger my-5" role="alert"
                     data-test="fishman-update-password-failed-required-information-missing">
                    Required information is missing. Please try the reset password link again, or request a new one.
                </div>
            `;
        }

        if (this.is_successful) {
            return html`
                <div class="alert alert-success my-5" role="alert" data-test="fishman-update-password-success">
                    Password update was successful, please wait while you are redirected.
                </div>
            `;
        }

        return html`

            <form class="row py-3 g-3" @submit=${this.onSubmit}>
                <div class="col-12 d-flex flex-column gap-3">

                    <h1 class="fishman-update__section-title text-center">
                        Update Password
                    </h1>

                    <div class="alert alert-info mb-0" role="alert" data-test="fishman-update-password-info">
                        Your new password must include:
                        <ul class="mb-0">
                            ${password_length ? html`
                                <li>At least ${password_length} characters</li>` : ''}
                            ${password_requirements?.map(req => html`
                                ${req === 'uppercase' ? html`
                                    <li>At least one uppercase letter</li>` : ''}
                                ${req === 'lowercase' ? html`
                                    <li>At least one lowercase letter</li>` : ''}
                                ${req === 'number' ? html`
                                    <li>At least one number</li>` : ''}
                                ${req === 'special_character' ? html`
                                    <li>At least one special character (!@#$%^&*()-+?~)</li>` : ''}
                            `)}
                        </ul>
                    </div>

                    <div>
                        <label for="new_password" class="form-label">New Password</label>
                        <input type="password" name="new_password" id="new-password" class="form-control"
                               pattern="${password_regex}" title="${error_message}" autocomplete="off"
                               data-test="new_password" required>
                    </div>

                    <div>
                        <label for="email" class="form-label">Email</label>
                        <input type="email" name="email" id="email" class="form-control" .value=${this.email} readonly
                               required/>
                    </div>

                    <div>
                        <label for="code" class="form-label">Code</label>
                        <input type="text" name="code" id="code" class="form-control" .defaultValue=${code}
                               autocomplete="off" required/>
                    </div>

                    ${this.is_form_submitting ? html`
                        <div class="alert alert-info mb-0" role="alert"
                             data-test="fishman-update-password-validating-password">
                            Validating password...
                        </div>
                    ` : ''}

                    ${this.errorMessage !== '' ? html`
                        <div class="alert alert-danger mb-0" role="alert"
                             data-test="fishman-update-password-failed-invalid-link"
                             .innerHTML=${this.errorMessage}/>                    ` : ''}

                    ${this.has_form_validation_error ? html`
                        <div class="alert alert-danger mb-0" role="alert"
                             data-test="fishman-update-password-failed-password-mismatch">
                            ${this.form_validation_error_message}
                        </div>
                    ` : ''}

                    <button type="submit" class="btn btn-primary align-self-center"
                            ?disabled=${this.is_form_submitting} data-test="submit">
                        Update Password
                    </button>

                </div>
            </form>
        `;
    }

    override createRenderRoot() {
        return this;
    }

    private async onSubmit(e: Event) {
        e.preventDefault();
        this.is_form_submitting = true;
        this.errorMessage = '';
        this.has_form_validation_error = false;
        this.form_validation_error_message = '';

        const form = e.target as HTMLFormElement;
        const formData = new FormData(form);

        // ensure the new password only contains our allowed characters, digits, and/or symbols (!@#$%^&*()-+?~)
        if (formData.get('new_password')?.toString().match(/[^A-Za-z0-9!@#\$%\^&\*\(\)\-\+\?~]/)) {
            this.has_form_validation_error = true;
            this.form_validation_error_message = 'Password contains invalid characters. Please only use the allowed characters, numbers, and/or symbols (!@#$%^&*()-+?~).';
            this.is_form_submitting = false;
            return;
        }

        const {block_id, redirect_url} = this.settings;

        const fetchProperties: FetchProps = {
            action: 'fishman_reset_password_via_ajax',
            block_id,
            method: 'POST',
            body: formData,
        };

        const {errorMessage, statusCode} = await a11Fetch(fetchProperties);

        if (statusCode === 200) {
            this.is_successful = true;
            window.location.href = redirect_url;
            return;
        }

        this.errorMessage = errorMessage;
        this.is_form_submitting = false;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'fishman-update-password': FishmanUpdatePassword;
    }

    interface Window {
        ai_site_data: A11SiteDataModel
    }
}
